/**
 * Contact — BCL Telecom & Énergie.
 * Coordonnées, formulaire de contact, horaires, zones d'intervention.
 */
import { Clock, Mail, MapPin, Phone, Send } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { MarketingLayout } from '@/components/site/marketing-layout';

const regions = [
  'Île-de-France',
  'Auvergne-Rhône-Alpes',
  'Provence-Alpes-Côte d\'Azur',
  'Nouvelle-Aquitaine',
  'Occitanie',
  'Hauts-de-France',
  'Grand Est',
  'Pays de la Loire',
  'Bretagne',
  'Normandie',
  'Centre-Val de Loire',
  'Bourgogne-Franche-Comté',
];

export function ContactPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name || !email || !subject || !message) {
      toast.error('Merci de renseigner les champs obligatoires');
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error('Adresse e-mail invalide');
      return;
    }
    setSubmitting(true);
    setTimeout(() => {
      toast.success(
        'Message envoyé. Nous revenons vers vous sous 24 h ouvrées.',
      );
      setName('');
      setEmail('');
      setPhone('');
      setSubject('');
      setMessage('');
      setSubmitting(false);
    }, 900);
  }

  return (
    <MarketingLayout>
      {/* HERO */}
      <section className="bg-gradient-to-b from-white to-bcl-grey/40 py-20">
        <div className="container px-4 mx-auto">
          <div className="mx-auto max-w-3xl text-center">
            <div className="inline-block rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-bcl-blue mb-4">
              Nous contacter
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6 text-balance">
              Parlons de votre projet de raccordement
            </h1>
            <p className="text-lg text-gray-600">
              Notre équipe vous répond sous 24 h ouvrées. Pour les urgences
              chantier, privilégiez la hotline téléphonique.
            </p>
          </div>
        </div>
      </section>

      {/* COORDONNÉES */}
      <section className="py-12 bg-white">
        <div className="container px-4 mx-auto">
          <div className="grid gap-4 md:grid-cols-3">
            <Card>
              <CardHeader className="flex flex-row items-start gap-4">
                <div className="grid h-10 w-10 place-items-center rounded-md bg-bcl-blue/10 text-bcl-blue">
                  <Phone className="h-5 w-5" />
                </div>
                <div>
                  <CardTitle className="text-base">Téléphone</CardTitle>
                  <p className="mt-1 text-sm text-gray-600">
                    +33 1 00 00 00 00
                  </p>
                  <p className="mt-1 text-xs text-gray-500">
                    Lun-Ven 9h-18h · Sam 9h-12h
                  </p>
                </div>
              </CardHeader>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-start gap-4">
                <div className="grid h-10 w-10 place-items-center rounded-md bg-bcl-blue/10 text-bcl-blue">
                  <Mail className="h-5 w-5" />
                </div>
                <div>
                  <CardTitle className="text-base">Email</CardTitle>
                  <p className="mt-1 text-sm text-gray-600">
                    contact@bcl-telecom.fr
                  </p>
                  <p className="mt-1 text-xs text-gray-500">
                    Réponse sous 24 h ouvrées
                  </p>
                </div>
              </CardHeader>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-start gap-4">
                <div className="grid h-10 w-10 place-items-center rounded-md bg-bcl-blue/10 text-bcl-blue">
                  <MapPin className="h-5 w-5" />
                </div>
                <div>
                  <CardTitle className="text-base">Siège</CardTitle>
                  <p className="mt-1 text-sm text-gray-600">
                    France métropolitaine
                  </p>
                  <p className="mt-1 text-xs text-gray-500">
                    Intervention nationale
                  </p>
                </div>
              </CardHeader>
            </Card>
          </div>
        </div>
      </section>

      {/* FORMULAIRE + INFOS */}
      <section className="py-16 bg-bcl-grey/30">
        <div className="container px-4 mx-auto">
          <div className="grid gap-8 lg:grid-cols-3">
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle>Envoyez-nous un message</CardTitle>
                <p className="text-sm text-gray-600">
                  Précisez votre adresse, la nature des travaux et votre
                  échéance pour une qualification plus rapide.
                </p>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-1.5">
                      <Label htmlFor="cname">
                        Nom complet <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="cname"
                        placeholder="Jean Dupont"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="cmail">
                        Email <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="cmail"
                        type="email"
                        placeholder="vous@exemple.fr"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                      />
                    </div>
                  </div>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-1.5">
                      <Label htmlFor="cphone">Téléphone</Label>
                      <Input
                        id="cphone"
                        type="tel"
                        placeholder="06 12 34 56 78"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="csubject">
                        Sujet <span className="text-red-500">*</span>
                      </Label>
                      <Select value={subject} onValueChange={setSubject}>
                        <SelectTrigger id="csubject">
                          <SelectValue placeholder="Sélectionnez un sujet" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="raccordement-individuel">
                            Raccordement individuel
                          </SelectItem>
                          <SelectItem value="raccordement-collectif">
                            Raccordement collectif
                          </SelectItem>
                          <SelectItem value="promoteur">
                            Promoteur / aménageur
                          </SelectItem>
                          <SelectItem value="copropriete">
                            Copropriété / syndic
                          </SelectItem>
                          <SelectItem value="suivi-dossier">
                            Suivi d&apos;un dossier en cours
                          </SelectItem>
                          <SelectItem value="autre">Autre</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="cmsg">
                      Message <span className="text-red-500">*</span>
                    </Label>
                    <Textarea
                      id="cmsg"
                      rows={6}
                      placeholder="Décrivez votre projet (adresse, type de logement, étape, échéance)…"
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      required
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="bg-bcl-blue hover:bg-bcl-blue/90"
                  >
                    <Send className="mr-2 h-4 w-4" />
                    {submitting ? 'Envoi en cours…' : 'Envoyer le message'}
                  </Button>
                </form>
              </CardContent>
            </Card>

            <div className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Clock className="h-4 w-4 text-bcl-blue" />
                    Horaires support
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-1 text-sm text-gray-600">
                  <p className="flex justify-between">
                    <span>Lundi - Vendredi</span>
                    <span className="font-medium text-gray-900">9h - 18h</span>
                  </p>
                  <p className="flex justify-between">
                    <span>Samedi</span>
                    <span className="font-medium text-gray-900">9h - 12h</span>
                  </p>
                  <p className="flex justify-between">
                    <span>Dimanche</span>
                    <span className="font-medium text-gray-900">Fermé</span>
                  </p>
                  <p className="mt-3 text-xs text-gray-500">
                    Hotline chantier active 7j/7 pour les clients en cours de
                    travaux.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <MapPin className="h-4 w-4 text-bcl-blue" />
                    Zones d&apos;intervention
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-600 mb-2">
                    Nous intervenons sur l&apos;ensemble des régions
                    métropolitaines :
                  </p>
                  <ul className="grid grid-cols-1 gap-1 text-xs text-gray-700">
                    {regions.map((r) => (
                      <li key={r} className="flex items-center gap-1.5">
                        <span className="inline-block h-1.5 w-1.5 rounded-full bg-bcl-teal" />
                        {r}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
